import {
  useLayoutEffect,
  useRef,
  useState,
  type RefCallback,
  type RefObject,
} from 'react';

export interface PillRect {
  left: number;
  width: number;
}

export interface UseSlidingPillResult<
  T extends HTMLElement,
  C extends HTMLElement = HTMLDivElement,
> {
  containerRef: RefObject<C | null>;
  registerItem: (index: number) => RefCallback<T>;
  pill: PillRect | null;
}

export function useSlidingPill<
  T extends HTMLElement,
  C extends HTMLElement = HTMLDivElement,
>(activeIndex: number): UseSlidingPillResult<T, C> {
  const containerRef = useRef<C>(null);
  const itemRefs = useRef<(T | null)[]>([]);
  const [pill, setPill] = useState<PillRect | null>(null);

  useLayoutEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const measure = () => {
      const item = itemRefs.current[activeIndex];

      if (!item) {
        setPill(null);

        return;
      }

      setPill({ left: item.offsetLeft, width: item.offsetWidth });
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);

    for (const item of itemRefs.current) {
      if (item) {
        observer.observe(item);
      }
    }

    return () => observer.disconnect();
  }, [activeIndex]);

  const registerItem =
    (index: number): RefCallback<T> =>
    el => {
      itemRefs.current[index] = el;
    };

  return { containerRef, registerItem, pill };
}
